import { useState } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

interface Sequence {
  id: string;
  difficulty: string;
  label: string;
  sequence: number[];
}

interface SequenceState {
  input: string;
  attempts: number;
  solved: boolean;
  revealed: boolean;
  explanation: string | null;
  correctAnswer: number | null;
  lastCorrect: boolean | null;
  loading: boolean;
  error: string | null;
}

interface Props {
  nanoid: string;
  sequences: Sequence[];
}

const MAX_ATTEMPTS = 3;

const DIFFICULTY_LABEL: Record<string, { color: string; bg: string }> = {
  easy:   { color: '#2d6e4e', bg: '#e8f5ee' },
  medium: { color: '#7a5200', bg: '#fff3d6' },
  hard:   { color: '#a02020', bg: '#fdecea' },
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function PatternPuzzle({ nanoid, sequences }: Props) {
  const [states, setStates] = useState<SequenceState[]>(
    sequences.map(() => ({
      input: '',
      attempts: 0,
      solved: false,
      revealed: false,
      explanation: null,
      correctAnswer: null,
      lastCorrect: null,
      loading: false,
      error: null,
    }))
  );

  function update(idx: number, patch: Partial<SequenceState>) {
    setStates(prev => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }

  async function checkAnswer(idx: number, reveal = false) {
    const state = states[idx];
    const answer = Number(state.input);

    if (!reveal && (!state.input.trim() || !Number.isFinite(answer))) return;

    // On the last attempt, always request the answer from the server so it's
    // available to display immediately if the guess is wrong.
    const isLastAttempt = !reveal && (state.attempts + 1 >= MAX_ATTEMPTS);

    update(idx, { loading: true, error: null, lastCorrect: null });

    try {
      const res = await fetch(`/api/puzzles/${nanoid}/pattern/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sequence_index: idx,
          answer: reveal ? -1 : answer,
          reveal: reveal || isLastAttempt,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed');

      const newAttempts = reveal ? state.attempts : state.attempts + 1;
      update(idx, {
        loading: false,
        lastCorrect: reveal ? null : data.data.correct,
        solved: data.data.correct === true,
        revealed: reveal || isLastAttempt || data.data.correct === true,
        explanation: data.data.explanation ?? null,
        correctAnswer: data.data.answer ?? null,
        attempts: newAttempts,
        input: state.input,
      });
    } catch {
      update(idx, { loading: false, error: 'Something went wrong. Try again.' });
    }
  }

  async function revealAnswer(idx: number) {
    await checkAnswer(idx, true);
  }

  return (
    <div className="pz-pattern">
      {sequences.map((seq, idx) => {
        const state = states[idx];
        const attemptsLeft = MAX_ATTEMPTS - state.attempts;
        const style = DIFFICULTY_LABEL[seq.difficulty] ?? DIFFICULTY_LABEL.easy;
        const done = state.solved || state.revealed;

        return (
          <div key={seq.id} className={`pz-sequence-card ${done ? 'pz-seq-done' : ''}`}>
            <div className="pz-seq-header">
              <span
                className="pz-difficulty-badge"
                style={{ color: style.color, background: style.bg }}
              >
                {seq.label}
              </span>
              {state.solved && <span className="pz-seq-result pz-seq-correct">Correct!</span>}
              {state.revealed && !state.solved && (
                <span className="pz-seq-result pz-seq-wrong">Revealed</span>
              )}
            </div>

            <div className="pz-sequence-display">
              {seq.sequence.map((n, i) => (
                <span key={i} className="pz-seq-num">{n}</span>
              ))}
              <span className="pz-seq-arrow">→</span>
              <span className="pz-seq-unknown">?</span>
            </div>

            {done ? (
              <div className="pz-seq-revealed">
                <p className="pz-seq-answer">
                  Answer: <strong>{state.correctAnswer}</strong>
                </p>
                {state.explanation && (
                  <p className="pz-seq-explanation">{state.explanation}</p>
                )}
              </div>
            ) : (
              <div className="pz-seq-input-row">
                <input
                  type="number"
                  className={`pz-number-input ${
                    state.lastCorrect === false ? 'pz-input-wrong' : ''
                  }`}
                  placeholder="Your answer"
                  value={state.input}
                  onChange={e => update(idx, { input: e.target.value, lastCorrect: null })}
                  disabled={state.loading || done}
                  onKeyDown={e => e.key === 'Enter' && checkAnswer(idx)}
                />
                <button
                  className="pz-btn-primary pz-btn-sm"
                  onClick={() => checkAnswer(idx)}
                  disabled={state.loading || !state.input.trim() || done}
                >
                  {state.loading ? '...' : 'Check'}
                </button>
              </div>
            )}

            {state.error && <p className="pz-error">{state.error}</p>}

            {!done && state.lastCorrect === false && (
              <div className="pz-attempt-feedback">
                <span className="pz-wrong-label">Not quite.</span>
                {attemptsLeft > 0 ? (
                  <span className="pz-attempts-left">
                    {attemptsLeft} attempt{attemptsLeft !== 1 ? 's' : ''} left
                  </span>
                ) : null}
                {attemptsLeft <= 0 && (
                  <button
                    className="pz-btn-ghost pz-btn-sm"
                    onClick={() => revealAnswer(idx)}
                  >
                    Show answer
                  </button>
                )}
              </div>
            )}

            {!done && state.attempts > 0 && attemptsLeft > 0 && attemptsLeft < MAX_ATTEMPTS && state.lastCorrect === null && (
              <div className="pz-attempt-feedback">
                <button
                  className="pz-btn-ghost pz-btn-sm"
                  onClick={() => revealAnswer(idx)}
                >
                  Give up & show answer
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
